import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  Card,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useStore } from "@tanstack/react-form";
import moment from "moment";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Option } from "api/common/types/common.types";
import { TDateISO } from "api/common/types/datetime.types";
import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useListMailCategoriesQuery,
  useUpdateMailListMutation,
} from "api/memberPortal/mailList/mailList.api";
import { MailingListDomains } from "api/memberPortal/mailList/mailList.types";
import { useAppForm } from "components/Forms/AppForm";

import { editMailListSchema } from "../../../MailList/MailList.schema";

import DetailCardCategory from "./components/DetailCardCategory";
import DetailCardDescription from "./components/DetailCardDescription";
import DetailCardTitle from "./components/DetailCardTitle";
import { DetailCardMode } from "./MailListDetails.types";

interface MailListDetailsCardProps {
  id: number;
  title: string;
  category: number;
  description: string;
  color: string;
  memberCount: number;
  lastUpdated: TDateISO;
  email: string;
  domain: string;
  mode: DetailCardMode;
  onSelectionClear: () => void;
  setMode: (_mode: DetailCardMode) => void;
}

export default function MailListDetailsCard({
  id,
  title,
  category,
  description,
  color,
  memberCount,
  lastUpdated,
  email,
  domain,
  mode,
  onSelectionClear,
  setMode,
}: MailListDetailsCardProps) {
  const { data: categoriesData } = useListMailCategoriesQuery({});
  const [updateMailList, { error, reset: resetMutation }] =
    useUpdateMailListMutation();

  const categoryOptions: Option[] =
    categoriesData?.results?.map((cat) => ({
      label: cat.name,
      value: String(cat.id),
    })) ?? [];

  const categoryLabel =
    categoriesData?.results?.find((cat) => cat.id === category)?.name ??
    "General";

  const form = useAppForm({
    defaultValues: {
      name: title,
      category: category,
      email: email,
      domain: domain,
      description: description,
    },

    validators: {
      onSubmit: editMailListSchema,
    },

    onSubmit: async ({ value }) => {
      const result = await updateMailList({
        id,
        name: value.name,
        email: value.email,
        domain: value.domain as MailingListDomains,
        description: value.description,
        categoryId: value.category,
      });

      if (!("error" in result)) {
        handleClose();
      }
    },
  });

  const isDirty = useStore(form.store, (state) => state.isDirty);

  const navigate = useNavigate();

  const handleDelete = (e: SyntheticEvent) => {
    e.stopPropagation();
    navigate(`delete/${id}`);
  };

  const handleClose = () => {
    setMode("view");
    form.reset();
    resetMutation();
  };

  const handleEdit = () => {
    form.reset();
    setMode("edit");
    onSelectionClear();
  };

  return (
    <Card sx={{ width: "100%", px: 4, py: 3 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Stack gap={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <DetailCardTitle title={title} mode={mode} form={form} />
              <DetailCardCategory
                categoryLabel={categoryLabel}
                mode={mode}
                color={color}
                categoryOptions={categoryOptions}
                form={form}
              />
            </Stack>

            {mode === "view" ? (
              <IconButton aria-label="Edit mailing list" onClick={handleEdit}>
                <EditIcon />
              </IconButton>
            ) : (
              <Stack direction="row" gap={2}>
                <IconButton
                  aria-label="Save mailing list"
                  type="submit"
                  color="primary"
                  disabled={!isDirty}
                >
                  <SaveIcon />
                </IconButton>
                <IconButton
                  aria-label="Close edit mailing list"
                  onClick={handleClose}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            )}
          </Stack>

          {/* Description row */}

          <DetailCardDescription
            description={description}
            mode={mode}
            form={form}
          />

          {/* Bottom row: stats + email */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" gap={5}>
              {/* Domain Type */}
              <Stack>
                <Typography fontWeight={700}>Domain</Typography>
                <Typography color="secondary">{domain}</Typography>
              </Stack>
              {/* Member count */}
              <Stack>
                <Typography fontWeight={700}>Member Count</Typography>
                <Typography color="secondary">{memberCount} members</Typography>
              </Stack>

              {/* Last updated */}
              <Stack>
                <Typography fontWeight={700}>Last Updated</Typography>
                <Typography color="secondary">
                  {moment(lastUpdated).format("MMM D, YYYY")}
                </Typography>
              </Stack>
            </Stack>

            {/* Email link */}
            <Link
              href={`mailto:${email}`}
              sx={{
                textDecoration: "underline",
              }}
            >
              {email}
            </Link>
          </Stack>

          {/* Error Occurred */}

          {error && (
            <Alert severity="error">{normalizeErrorMessages(error)}</Alert>
          )}

          {/* Delete Button */}
          <Box>
            {mode === "edit" && (
              <Button
                color="error"
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                Delete Mailing List
              </Button>
            )}
          </Box>
        </Stack>
      </form>
    </Card>
  );
}
